import React, { useState } from 'react';
import moment from 'moment';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import Calendar from '../calendar';
import { setMonth, monthMap, formatAndMerge, DateDropDownFlipper } from '../logic';
import DateDropDowns from '../date-drop-downs';
import makeStyles from './styles';

type picker = {
  rangeSelectionHandler: Function;
  close: Function;
  inputTimeRange?: string;
  blockFutureDates?: boolean;
};

const RangePicker = ({
  rangeSelectionHandler,
  close,
  inputTimeRange,
  blockFutureDates,
}: picker) => {
  const date = new Date();
  const today = new Date(date);
  const defaultFirstSelected = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
  ).getTime();
  const tomorrow = new Date(date);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultSecondSelected = blockFutureDates ? new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    today.getHours(),
    today.getMinutes()
  ).getTime() :
  new Date(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate(),
    23,
    59,
  ).getTime();

  const blockDefaultFutureTime = blockFutureDates ? new Date(
    today.getHours(),
    today.getMinutes()
  ).getTime() : { hours: 23, minutes: 59 };
  const timeRange = inputTimeRange?.split('-') || [];
  const inputFirstDate =
    timeRange.length > 1 ? moment.utc(timeRange[0], 'MM/DD/YYYY HH:mm')  : undefined;
  const inputSecondDate =
    timeRange.length > 1 ? moment.utc(timeRange[1], 'MM/DD/YYYY HH:mm') : undefined;
  const inputFirstSelected = inputFirstDate ? inputFirstDate.valueOf() : defaultFirstSelected;
  const inputFirstSelectedTime = inputFirstDate
    ? { hours: inputFirstDate.hour(), minutes: inputFirstDate.minutes() }
    : { hours: 0, minutes: 0 };
  const inputSecondSelected = inputSecondDate ? inputSecondDate.valueOf() : defaultSecondSelected;
  const inputSecondSelectedTime = inputSecondDate
    ? { hours: inputSecondDate.hour(), minutes: inputSecondDate.minutes() }
    : blockDefaultFutureTime;
  const [calYear, setCalYear] = useState(date.getFullYear());
  const [calMonth, setCalMonth] = useState(date.getMonth());
  const [firstSelected, setFirstSelected] = useState<number | undefined>(inputFirstSelected);
  const [firstSelectedTime, setFirstSelectedTime] = useState<any>(inputFirstSelectedTime);
  const [secondSelected, setSecondSelected] = useState<number | undefined>(inputSecondSelected);
  const [secondSelectedTime, setSecondSelectedTime] = useState<any>(inputSecondSelectedTime);
  const [startOrEndDate, setStartOrEndDate] = useState<boolean>(true);
  const classes = makeStyles();
  const resetAll = () => {
    setCalYear(date.getFullYear());
    setCalMonth(date.getMonth());
    setFirstSelected(undefined);
    setFirstSelectedTime({});
    setSecondSelected(undefined);
    setSecondSelectedTime({});
    setStartOrEndDate(true);
  };
  const findIdenticalTimes = () => {
    if (firstSelected === secondSelected) {
      const identicalHours =
        firstSelectedTime.hours && firstSelectedTime.hours === secondSelectedTime.hours;
      const identicalMinutes =
        firstSelectedTime.minutes && firstSelectedTime.minutes === secondSelectedTime.minutes;
      if (
        (identicalHours && identicalMinutes === undefined) ||
        (identicalHours && identicalMinutes) === true
      )
        return false;
      return true;
    }
    return true;
  };

  const dropDownOrder = DateDropDownFlipper({
    firstSelected,
    setFirstSelected,
    secondSelected,
    setSecondSelected,
    firstSelectedTime,
    secondSelectedTime,
    setFirstSelectedTime,
    setSecondSelectedTime,
  });
  const selectorProperties = (activeOn: boolean) => ({
    tabIndex:0,
    role:"button",
    onClick: () => {setStartOrEndDate(activeOn)},
    });
  return (
    <Box>
      <Box p={2} display="flex" flexDirection="row" justifyContent="space-around">
        <fieldset {...selectorProperties(true)} className={(startOrEndDate === true)?  `${classes.activeBorder} ${classes.dateBlock}`: classes.dateBlock}>
        <legend>Start</legend>
        <DateDropDowns
          date={date}
          selected={dropDownOrder.left.selected}
          setSelected={dropDownOrder.left.setSelected}
          time={dropDownOrder.left.time}
          setTime={dropDownOrder.left.setTime}
        />
        </fieldset>
        <fieldset {...selectorProperties(false)} className={(startOrEndDate === false)? `${classes.activeBorder} ${classes.dateBlock}`: classes.dateBlock}>
        <legend>End</legend>
        <DateDropDowns
          date={date}
          selected={dropDownOrder.right.selected}
          setSelected={dropDownOrder.right.setSelected}
          time={dropDownOrder.right.time}
          setTime={dropDownOrder.right.setTime}
        />
        </fieldset>
      </Box>
      <Divider />
      <Box
        p={2}
        pb={0}
        display="flex"
        flexDirection="row"
        justifyContent="space-around"
        alignItems="center"
      >
        <IconButton
          size="small"
          onClick={() => setMonth(-1, calYear, calMonth, setCalMonth, setCalYear)}
          className={classes.iconLeft}
        >
          <ExpandMore />
        </IconButton>
        <Box pr={2}>
          <Typography variant="h5">
            {calMonth === 0 ? monthMap[11] : monthMap[calMonth - 1]}
          </Typography>
        </Box>
        <Box pl={2}>
          <Typography variant="h5">{monthMap[calMonth]}</Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setMonth(1, calYear, calMonth, setCalMonth, setCalYear)}
          className={classes.iconRight}
        >
          <ExpandMore />
        </IconButton>
      </Box>
      <Box display="flex" flexDirection="row">
        <Calendar
          blockFutureDates={blockFutureDates}
          year={calYear}
          month={calMonth - 1}
          firstSelected={firstSelected}
          setFirstSelected={setFirstSelected}
          secondSelected={secondSelected}
          setSecondSelected={setSecondSelected}
          startOrEndDate={startOrEndDate}
          setStartOrEndDate={setStartOrEndDate}
        />
        <Calendar
          blockFutureDates={blockFutureDates}
          year={calYear}
          month={calMonth}
          firstSelected={firstSelected}
          setFirstSelected={setFirstSelected}
          secondSelected={secondSelected}
          setSecondSelected={setSecondSelected}
          startOrEndDate={startOrEndDate}
          setStartOrEndDate={setStartOrEndDate}
        />
      </Box>
      <Divider />
      <Box p={2} display="flex" justifyContent="flex-end">
        <Box display="flex">
          <Button
            onClick={() => close(false)}
            size="small"
            variant="outlined"
            style={{ marginRight: '1rem' }}
          >
            Cancel
          </Button>
          {(firstSelected || secondSelected) && (
            <Button
              onClick={() => resetAll()}
              size="small"
              variant="outlined"
              style={{ marginRight: '1rem' }}
            >
              Reset
            </Button>
          )}
          <Button
            className={classes.applyButton}
            onClick={() =>
              rangeSelectionHandler(
                formatAndMerge(
                  firstSelectedTime,
                  secondSelectedTime,
                  firstSelected,
                  secondSelected,
                  blockFutureDates,
                ),
              )
            }
            disabled={!(!!firstSelected && !!secondSelected && findIdenticalTimes())}
            size="small"
            variant="contained"
            color="secondary"
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RangePicker;
